import { motion } from 'framer-motion';
import { Diamond, Video, Users, MessageCircle, Megaphone, Youtube, Instagram, ExternalLink } from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: Video,
      title: 'UGC Creation',
      description: 'Authentic user-generated content that resonates with audiences and drives engagement.',
      color: '#9D4EDD'
    },
    {
      icon: MessageCircle,
      title: 'Chat Moderation',
      description: 'Professional community management and live chat moderation for streams and social platforms.',
      color: '#39FF14'
    },
    {
      icon: Users,
      title: 'Social Moderation',
      description: 'Building and maintaining healthy online communities with expert social media moderation.',
      color: '#9D4EDD'
    },
    {
      icon: Megaphone,
      title: 'Promotional Streaming',
      description: 'High-energy promotional streams that showcase products and engage viewers.',
      color: '#39FF14'
    }
  ];

  const stats = [
    { label: 'Community Members', value: '10K+' },
    { label: 'Hours Streamed', value: '500+' },
    { label: 'Partner Campaigns', value: '20+' }
  ];

  const socialLinks = [
    { icon: Youtube, label: 'YouTube', url: 'https://youtube.com/@relatableshayla?si=QV-whWVG5RggPgsz', color: '#FF0000' },
    { icon: Instagram, label: 'Instagram', url: 'https://www.instagram.com/relatableshayla?igsh=bnR1Z3ZzbzZkMnU2', color: '#E4405F' },
    { 
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      ), 
      label: 'TikTok', 
      url: 'https://tiktok.com/relatableshayla', 
      color: '#000000' 
    },
    { 
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M11.64 5.93h1.43v4.28h-1.43m3.93-4.28H17v4.28h-1.43M7 2L3.43 5.57v12.86h4.28V22l3.58-3.57h2.85L20.57 12V2m-1.43 9.29l-2.85 2.85h-2.86l-2.5 2.5v-2.5H7.36V3.43h11.78z"/>
        </svg>
      ), 
      label: 'Twitch', 
      url: 'https://twitch.tv/Relatableshayla', 
      color: '#9146FF' 
    },
    { 
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M10.37 5.21a4.68 4.68 0 0 1 3.87.76l.42.33 1.9-1.9a7.5 7.5 0 0 0-8.77-.59c-.67.43-1.28.97-1.79 1.6L7.9 7.3a4.69 4.69 0 0 1 2.47-2.09zM4.74 9.7a7.5 7.5 0 0 0 1.1 6.31l1.9-1.9a4.68 4.68 0 0 1 .16-4.76L5.99 7.44a7.5 7.5 0 0 0-1.25 2.26zM12 16.5a4.5 4.5 0 0 1-3.18-1.32l-1.9 1.9A7.48 7.48 0 0 0 12 19.5a7.5 7.5 0 0 0 5.08-1.97l-1.9-1.9A4.5 4.5 0 0 1 12 16.5zM16.89 8.54l1.9-1.9A7.5 7.5 0 0 0 12 4.5a7.45 7.45 0 0 0-2.36.38l.86 2.56A4.7 4.7 0 0 1 12 7.05a4.5 4.5 0 0 1 3.18 1.31l1.71.18z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      ), 
      label: 'Kick', 
      url: 'https://kick.com/relatableshayla', 
      color: '#53FC18' 
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="home-page">
      {/* Decorative blobs */}
      <div className="glow-blob glow-blob-purple top-20 -left-48 fixed" />
      <div className="glow-blob glow-blob-green bottom-20 -right-48 fixed" />

      <div className="max-w-7xl mx-auto relative">
        {/* Hero Section */}
        <motion.section 
          className="grid lg:grid-cols-2 gap-12 items-center mb-24"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Text Content */}
          <motion.div variants={itemVariants} className="order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              <Diamond className="w-5 h-5 text-[#39FF14] sparkle" />
              <span className="text-[#39FF14] font-medium uppercase tracking-wider text-sm">Content Creator & Streamer</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-6 leading-tight">
              <span className="text-[#E0E0E0]">Hey, I'm</span>
              <br />
              <span className="text-glow-purple text-[#9D4EDD]">RelatableShayla</span>
            </h1>
            
            <p className="text-lg text-gray-400 mb-8 max-w-xl leading-relaxed">
              Your go-to partner for authentic UGC creation, community building, and promotional streaming. 
              Let's create something amazing together and bring your brand to life with engaging content 
              that connects with real audiences.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="/contact" 
                className="btn-primary px-8 py-3 rounded-full font-semibold inline-flex items-center gap-2"
                data-testid="cta-work-with-me"
              >
                Work With Me
                <ExternalLink className="w-4 h-4" />
              </a>
              <a 
                href="/experience" 
                className="btn-ghost px-8 py-3 rounded-full font-semibold"
                data-testid="cta-view-experience"
              >
                View Experience
              </a>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div 
            variants={itemVariants}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#9D4EDD] rounded-full blur-3xl opacity-20 scale-110" />
              <img
                src="https://customer-assets.emergentagent.com/job_ugc-marketing-brand/artifacts/vsm0uh61_ChatGPT%20Image%20Jan%2020%2C%202026%2C%2001_17_48%20PM.png"
                alt="RelatableShayla"
                className="relative w-72 h-72 sm:w-96 sm:h-96 object-cover rounded-full image-glow"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -right-4 glass-card px-4 py-2 flex items-center gap-2">
                <Diamond className="w-4 h-4 text-[#39FF14]" />
                <span className="font-semibold text-sm">The Relatable Family</span>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* Stats Section */}
        <motion.section 
          className="mb-24"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="glass-card p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                variants={itemVariants}
                className="text-center"
              >
                <div className="stat-number">{stat.value}</div>
                <div className="text-gray-400 uppercase tracking-wider text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Services Section */}
        <motion.section 
          className="mb-24"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">What I</span>{' '}
              <span className="text-[#9D4EDD]">Offer</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From content creation to community management, I provide comprehensive services 
              to help brands connect with their audience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="service-card p-8"
                data-testid={`service-card-${index}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${service.color}20` }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color }} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-[#E0E0E0]">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Social Links Section */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase mb-4">
              <span className="text-[#E0E0E0]">Connect</span>{' '}
              <span className="text-[#39FF14]">With Me</span>
            </h2>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-4"
          >
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon group"
                data-testid={`social-link-${social.label.toLowerCase()}`}
                title={social.label}
              >
                {typeof social.icon === 'function' ? (
                  <social.icon />
                ) : (
                  <social.icon className="w-5 h-5" />
                )}
              </a>
            ))}
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default HomePage;
